import type { EmployeeCompetencyOverlayPayload } from '@/api/competency-services/employee-competency/employeeCompetencyService';
import type {
    ProfileAssignmentPayload,
    RosterEmployee,
} from '@/api/competency-services/profile-assignment/profileAssignmentService';
import type { ProfileSelectorResult } from '@/components/CompetencyProfileAssignment/ProfileSelector';
import type { TranslationKey } from '@/i18n-key';
import type { QueryClient } from '@tanstack/react-query';

import performanceProfileService from '@/api/performance-profile/performanceProfileService';
import { fullName } from '@/components/CompetencyProfileAssignment/helpers';
import {
    CompetencyProfileQueryKey,
    DirectoryQueryKey,
    PerformanceProfileQueryKey,
    ProfileAssignmentQueryKey,
} from '@/constants/QueryKeys';
import { showSuccessToast } from '@/plugins/Toast';

type TranslateFn = (key: TranslationKey, options?: Record<string, unknown>) => string;

type AssignProfileFn = (args: {
    profileId: string;
    payload: ProfileAssignmentPayload;
}) => Promise<unknown>;

type SetOverlayFn = (payload: EmployeeCompetencyOverlayPayload) => Promise<unknown>;

/** Refresh assignment read models on the active QueryClient (not a global singleton). */
const invalidateAssignmentCaches = async (queryClient: QueryClient, employeeId?: string) => {
    const invalidations = [
        queryClient.invalidateQueries({
            queryKey: [PerformanceProfileQueryKey.COMPETENCY_ASSIGNMENT],
        }),
        queryClient.invalidateQueries({
            queryKey: [ProfileAssignmentQueryKey.ROSTER],
        }),
    ];

    if (employeeId) {
        invalidations.push(
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.SKILLSET_ASSESSMENT, employeeId],
            }),
            queryClient.invalidateQueries({
                queryKey: [DirectoryQueryKey.USER_BY_ID, employeeId],
            }),
        );
    }

    await Promise.all(invalidations);
};

export type ConfirmEmployeeProfileSelectionArgs = {
    employee: RosterEmployee;
    result: ProfileSelectorResult;
    assignProfile: AssignProfileFn;
    setOverlay: SetOverlayFn;
    queryClient: QueryClient;
    t: TranslateFn;
};

/**
 * Assign a profile, persist the employee's full overlay (added/removed sets), and record the levels
 * chosen for them when the selector offered any.
 */
export const confirmEmployeeProfileSelection = async ({
    employee,
    result,
    assignProfile,
    setOverlay,
    queryClient,
    t,
}: ConfirmEmployeeProfileSelectionArgs): Promise<void> => {
    await assignProfile({
        profileId: result.profileId,
        payload: { employeeIds: [employee.id] },
    });

    // Persist this employee's overlay as the FULL desired state. Sent whenever
    // editing an already-assigned employee (so an emptied overlay actually clears
    // stale rows) or whenever there is a customization to record.
    const isEditingAssigned = Boolean(employee.assignedProfileId);
    if (
        isEditingAssigned ||
        result.addedCompetencyIds.length > 0 ||
        result.removedCompetencyIds.length > 0
    ) {
        await setOverlay({
            employeeId: employee.id,
            addedCompetencyIds: result.addedCompetencyIds,
            removedCompetencyIds: result.removedCompetencyIds,
        });
    }

    const levels = Object.entries(result.competencyProficiencyLevels ?? {});
    if (levels.length > 0) {
        await Promise.all(
            levels.map(([competencyId, proficiencyLevelId]) =>
                performanceProfileService.saveCompetencyProficiencyHistory({
                    employeeId: employee.id,
                    competencyId,
                    proficiencyLevelId,
                }),
            ),
        );
        // The profile's groups carry the employee's levels: the next opening must read the new ones.
        await queryClient.invalidateQueries({
            queryKey: [CompetencyProfileQueryKey.COMPETENCY_PROFILES],
            predicate: (query) => query.queryKey.includes('groups'),
        });
    }

    await invalidateAssignmentCaches(queryClient, employee.id);

    const isCustomizationOnly =
        isEditingAssigned && employee.assignedProfileId === result.profileId;
    showSuccessToast({
        message: t(
            isCustomizationOnly
                ? 'competencies.competencyProfilesAdmin.toast.customizationSaved'
                : 'competencies.competencyProfilesAdmin.toast.profileAssigned',
            { name: fullName(employee) },
        ),
    });
};

export type CopyProfileFromEmployeeArgs = {
    employee: RosterEmployee;
    sourceEmployeeId: string;
    copyCandidates: RosterEmployee[];
    assignProfile: AssignProfileFn;
    setOverlay: SetOverlayFn;
    queryClient: QueryClient;
    t: TranslateFn;
};

/** Copy another employee's assigned profile + overlay onto the target. */
export const copyProfileFromEmployee = async ({
    employee,
    sourceEmployeeId,
    copyCandidates,
    assignProfile,
    setOverlay,
    queryClient,
    t,
}: CopyProfileFromEmployeeArgs): Promise<boolean> => {
    const source = copyCandidates.find((candidate) => candidate.id === sourceEmployeeId);
    if (!source?.assignedProfileId) {
        showSuccessToast({
            message: t('competencies.competencyProfilesAdmin.toast.copyNoProfile'),
        });

        return false;
    }

    await assignProfile({
        profileId: source.assignedProfileId,
        payload: { employeeIds: [employee.id] },
    });

    // Match Admin develop behavior: also copy the source employee's overlay when present.
    if (source.addedCompetencyIds.length > 0 || source.removedCompetencyIds.length > 0) {
        await setOverlay({
            employeeId: employee.id,
            addedCompetencyIds: source.addedCompetencyIds,
            removedCompetencyIds: source.removedCompetencyIds,
        });
    }

    await invalidateAssignmentCaches(queryClient, employee.id);

    showSuccessToast({
        message: t('competencies.competencyProfilesAdmin.toast.profileAssigned', {
            name: fullName(employee),
        }),
    });

    return true;
};
